import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useRouter } from "@/lib/router-helpers";
import TiketModal from "@/components/tiket-modal";
import BookingHistorySkeleton from "@/components/booking-history-skeleton";
import { toEthiopian } from "ethiopian-calendar-new";
import { getDay, getEthiopianMonth, toEthiopianTime } from "@/lib/ethiopian-date-conversion";
import { motion, AnimatePresence, Variants } from "framer-motion";
import { Search, Filter, Calendar, Clock, MapPin, ChevronRight, AlertCircle, History, Ticket, CalendarClock } from "lucide-react";
import { cn } from "@/lib/utils";
import Cookies from "js-cookie";

type Booking = {
  id: string;
  branch: string;
  category: string;
  datetime: Date;
  cnr: string;
  status: "completed" | "expired" | "upcoming" | "booked";
  raw?: any;
  firstName?: string;
  lastName?: string;
};

const mapApiItemToBooking = (item: any): Booking => {
  const id = item.appointment_id;
  const branch = item.branch_name;
  const category = item.service_name;
  const datetime = item.appointment_time;
  const cnr = item.cnr;
  const firstName = item.firstname;
  const lastName = item.lastname;

  let status: Booking["status"] = "upcoming";
  const s = (item.status ?? "").toString().toLowerCase();
  if (s === "incompleted" || s === "expired") {
    status = "expired";
  } else if (s === "completed" || s.includes("checked out")) {
    status = "completed";
  } else if (s === "confirmed") {
    status = "upcoming";
  } else {
    status = "booked";
  }
  
  // Check if the appointment time has passed
  const appointmentDate = new Date(datetime);
  const now = new Date();
  
  // If the appointment is in the past and currently marked as upcoming or booked, mark as expired
  if (appointmentDate < now && (status === "upcoming" || status === "booked")) {
    status = "expired";
  }

  return { id, branch, category, datetime, cnr, status, raw: item, firstName, lastName };
};

const fetchAppointments = async ({
  page,
  limit,
  searchQuery,
}: {
  page: number;
  limit: number;
  searchQuery: string;
}) => {
  const token = typeof window !== "undefined" ? Cookies.get("qetero.token") : null;
  const base = `${import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL}/v1/appointments/findAppointmentsByUser`;
  const q = `created_by=undefined&page=${page}&limit=${limit}&searchQuery=${encodeURIComponent(
    searchQuery ?? ""
  )}`;


  console.log('Fetching appointments with query token:', token);

  const res = await fetch(`${base}?${q}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "Failed to fetch appointments");
  }
  const json = await res.json();
  const items = json?.data;
  const bookings = Array.isArray(items.data) ? items.data.map(mapApiItemToBooking) : [];
  return bookings;
};

function formatDateTime(dateTime: Date, language: string): string {
  const dateObj = new Date(dateTime);
  if (language === "am" || language === "oro") {
    const etDate = toEthiopian(dateObj.getFullYear(), dateObj.getMonth() + 1, dateObj.getDate());
    const monthName = getEthiopianMonth(etDate.month);
    return `${monthName}, ${etDate.day} ${etDate.year}`;
  } else {
    return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}

function formatTime(dateTime: Date, language: string): string {
  const date = new Date(dateTime);
 
     const timeString = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
     const [time, modifier] = timeString.split(' ');
     return toEthiopianTime(time, modifier);
 
  //return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
};

export default function BookingHistoryPage() {
  const [activeTab, setActiveTab] = useState<"all" | "upcoming" | "completed">("all");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page] = useState(1);
  const [limit] = useState(10);
  const [selected, setSelected] = useState<Booking | null>(null);
  const { t, i18n } = useTranslation();
  const router = useRouter();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 400);
    return () => clearTimeout(t);
  }, [query]);
  console.log('Debounced Query:', debouncedQuery);

  const { data: bookings = [], isLoading, isError, refetch } = useQuery({
    queryKey: ["appointments", page, limit, debouncedQuery],
    queryFn: () => fetchAppointments({ page, limit, searchQuery: debouncedQuery }),
    staleTime: 1000 * 30,
  });

  const filtered = useMemo(() => {
    return bookings.filter((b: Booking) => {
      if (activeTab === "upcoming" && b.status !== "upcoming") return false;
      if (activeTab === "completed" && b.status !== "completed") return false;
      if (!debouncedQuery) return true;
      const q = debouncedQuery.toLowerCase();
      return (
        b.cnr?.toLowerCase().includes(q) ||
        b.branch?.toLowerCase().includes(q) ||
        b.category?.toLowerCase().includes(q)
      );
    });
  }, [bookings, activeTab, debouncedQuery]);

  const closeModal = () => setSelected(null);

  const handleReschedule = (e: React.MouseEvent, booking: Booking) => {
    e.stopPropagation();
    if (typeof window !== 'undefined') {
        window.localStorage.setItem("qetero.selectedService", booking.raw?.branch_service_id || "");
        window.localStorage.setItem("qetero.selectedServiceName", booking.category || "");
        window.localStorage.setItem("qetero.selectedBranchName", booking.branch || "");
    }

    const params = new URLSearchParams({
        mode: "reschedule",
        appointment_id: booking.id,
    });
    router.push(`/organization/dars?${params.toString()}`);
  };

  const tabs = [
    { id: "all", label: t("all") },
    { id: "upcoming", label: t("upcoming") },
    { id: "completed", label: t("completed") },
  ] as const;

  return (
    <div className="min-h-screen bg-gray-50/30 md:pb-20 pb-10 font-inter">
      <main className=" mx-auto px-1 md:px-4 pt-6">
        <div className="flex items-center justify-center gap-2 mb-6">
          <History className="w-5 h-5 text-pink-600" />
          <h2 className="text-lg font-bold text-gray-900">{t("bookingHistory")}</h2>
        </div>

        {/* Tabs */}
        <div className="flex p-1 bg-gray-100/80 rounded-2xl relative mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex-1 relative py-2.5 text-sm font-semibold rounded-xl z-10 transition-colors duration-200",
                activeTab === tab.id ? "text-white" : "text-gray-500 hover:text-gray-700"
              )}
            >
              {activeTab === tab.id && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute inset-0 bg-pink-600 rounded-xl shadow-sm shadow-pink-200/50 -z-10"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="flex gap-3 items-center mb-6">
          <div className="flex-1 bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100 flex items-center focus-within:ring-2 focus-within:ring-pink-100 focus-within:border-pink-200 transition-all">
            <Search className="w-5 h-5 text-gray-400 mr-3" />
            <input
              className="w-full flex-1 outline-none text-sm text-gray-700 bg-transparent placeholder:text-gray-400"
              placeholder={t("searchPlaceHolder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {/* <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="filter"
            className="w-12 h-12 rounded-2xl bg-pink-50 border border-pink-100 shadow-sm flex items-center justify-center hover:bg-pink-100 transition-colors"
            onClick={() => refetch()}
          >
            <Filter className="w-5 h-5 text-pink-600" />
          </motion.button> */}
        </div>

        {/* Content */}
        <div className="space-y-4">
          {isLoading ? (
            <BookingHistorySkeleton />
          ) : isError ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-12 text-center"
            >
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-gray-900 font-bold mb-1">{t("failedToLoadBookings")}</h3>
              <p className="text-gray-500 text-sm mb-4">{t("errorFetchingData")}</p>
              <button 
                onClick={() => refetch()}
                className="text-pink-600 font-semibold text-sm hover:underline"
              >
                {t("tryAgain")}
              </button>
            </motion.div>
          ) : filtered.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-12 text-center"
            >
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <Calendar className="w-10 h-10 text-gray-300" />
              </div>
              <h3 className="text-gray-900 font-bold mb-1">{t("noBookingFound")}</h3>
              <p className="text-gray-500 text-sm">
                {activeTab === 'all' 
                  ? t("noAppointmentsYet")
                  : t("noAppointmentsFoundForTab", { tab: t(activeTab) })}
              </p>
            </motion.div>
          ) : (
            <motion.div
              key={activeTab}
              variants={containerVariants}
              initial="hidden"
              animate="show"
              className="space-y-4"
            >
              {filtered.map((b: Booking) => (
                <motion.div
                  key={b.id}
                  variants={itemVariants}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => setSelected(b)}
                  className="group bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md hover:border-pink-100 transition-all cursor-pointer relative overflow-hidden flex flex-col"
                >
                  {/* Status Indicator Line */}
                  <div className={cn(
                    "absolute left-0 top-0 bottom-0 w-1 z-10",
                    b.status === "completed" ? "bg-emerald-500" :
                    b.status === "expired" ? "bg-gray-300" : "bg-pink-500"
                  )} />

                  <div className="p-4 pb-2 pl-5 relative">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider",
                          b.status === "completed" ? "bg-emerald-50 text-emerald-600 border border-emerald-100" :
                          b.status === "expired" ? "bg-gray-100 text-gray-500 border border-gray-200" :
                          "bg-pink-50 text-pink-600 border border-pink-100"
                        )}>
                          {t(b.status)}
                        </span>
                        <span className="text-xs text-gray-400 font-medium">#{b.cnr}</span>
                      </div>
                      
                      {/* Chevron */}
                      <div className="w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-pink-50 group-hover:text-pink-600 transition-colors">
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </div>

                    {/* Main Info */}
                    <h3 className="font-bold text-gray-900 text-base mb-1 truncate pr-4">{t(b.category)}</h3>
                    <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-2">
                      <MapPin className="w-3.5 h-3.5" />
                      <span className="truncate">{t(b.branch)} {t('branch')}</span>
                    </div>
                  </div>

                  {/* Footer Info */}
                  <div className="mt-auto px-4 pb-3 pl-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3 text-xs font-medium text-gray-500 w-full sm:w-auto mb-2">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        <span>{formatDateTime(b.datetime, i18n.language)}</span>
                      </div>
                      <div className="w-px h-3 bg-gray-300" />
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        <span>{formatTime(b.datetime, i18n.language)}</span>
                      </div>
                    </div>

                    {b.status === "upcoming" && (
                      (() => {
                        const now = new Date();
                        const appointmentDate = new Date(b.datetime);
                        const diffInMs = appointmentDate.getTime() - now.getTime();
                        const diffInHours = diffInMs / (1000 * 60 * 60);
                        const isDisabled = diffInHours < 3;

                        return (
                          <motion.button
                            whileHover={!isDisabled ? { scale: 1.05 } : {}}
                            whileTap={!isDisabled ? { scale: 0.95 } : {}}
                            onClick={(e) => !isDisabled && handleReschedule(e, b)}
                            disabled={isDisabled}
                            className={cn(
                              "w-full sm:w-auto flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors shadow-sm z-10",
                              isDisabled 
                                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed" 
                                : "bg-white text-pink-600 border-pink-200 hover:bg-pink-600 hover:text-white hover:border-pink-600"
                            )}
                            title={isDisabled ? t("rescheduleTooLate") : t("reschedule")}
                          >
                            <CalendarClock className="w-3.5 h-3.5" />
                            <span>{t("reschedule")}</span>
                          </motion.button>
                        );
                      })()
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </main>

      {/* Ticket modal */}
      <AnimatePresence>
        {selected && (
          <TiketModal selected={selected} closeModal={closeModal} />
        )}
      </AnimatePresence>
    </div>
  );
}
// ...existing code...